import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import isAdministrator from "../utilities/checkPrivileges";
import deviceGroupService from "../services/deviceGroupService";
import locationService from "../services/locationService";
import nightRestService from "../services/nightRestService";
import { BUILTIN_IOS_APPS, appByBundleId } from "../components/profileEditor/builtinIosApps";

const WEEKDAYS = [
  { id: 1, label: "Mo" },
  { id: 2, label: "Di" },
  { id: 3, label: "Mi" },
  { id: 4, label: "Do" },
  { id: 5, label: "Fr" },
  { id: 6, label: "Sa" },
  { id: 7, label: "So" },
];

const DEFAULT_ALLOW = ["com.apple.Preferences", "com.apple.mobilephone"];

const emptyForm = {
  name: "",
  enabled: true,
  timezone: "Europe/Berlin",
  startTime: "22:00",
  endTime: "07:00",
  daysOfWeek: [1, 2, 3, 4, 5, 6, 7],
  holidayDatesText: "",
  location: "",
  groups: [],
  udids: [],
  allowListedAppBundleIDs: [...DEFAULT_ALLOW],
  extraBundleId: "",
  manualOverride: "schedule",
};

function lines(text) {
  return String(text || "")
    .split(/[\n,;]+/)
    .map((value) => value.trim())
    .filter(Boolean);
}

function formatNextChange(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function statusBadge(policy) {
  if (!policy.enabled) return { className: "text-bg-secondary", label: "aus" };
  if (policy.manualOverride === "on") return { className: "text-bg-danger", label: "manuell an" };
  if (policy.manualOverride === "off") return { className: "text-bg-warning", label: "manuell aus" };
  if (policy.activeNow) return { className: "text-bg-danger", label: "aktiv" };
  return { className: "text-bg-success", label: "tagsüber" };
}

function formFromPolicy(policy) {
  return {
    name: policy.name || "",
    enabled: policy.enabled !== false,
    timezone: policy.timezone || "Europe/Berlin",
    startTime: policy.startTime || "22:00",
    endTime: policy.endTime || "07:00",
    daysOfWeek: Array.isArray(policy.daysOfWeek) && policy.daysOfWeek.length
      ? policy.daysOfWeek
      : [1, 2, 3, 4, 5, 6, 7],
    holidayDatesText: (policy.holidayDates || []).join("\n"),
    location: policy.location?._id || policy.location || "",
    groups: (policy.groups || []).map((group) => String(group._id || group)),
    udids: Array.isArray(policy.udids) ? policy.udids : [],
    allowListedAppBundleIDs: Array.isArray(policy.allowListedAppBundleIDs) && policy.allowListedAppBundleIDs.length
      ? policy.allowListedAppBundleIDs
      : [...DEFAULT_ALLOW],
    extraBundleId: "",
    manualOverride: policy.manualOverride || "schedule",
  };
}

export default function NightRest() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const canEdit = isAdministrator();
  const [policies, setPolicies] = useState([]);
  const [groups, setGroups] = useState([]);
  const [locations, setLocations] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deviceQuery, setDeviceQuery] = useState("");
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [policyRows, groupRows, locationRows, deviceRows] = await Promise.all([
        nightRestService.listPolicies(),
        deviceGroupService.getDeviceGroups().catch(() => []),
        locationService.getLocations().catch(() => []),
        nightRestService.listDevices().catch(() => []),
      ]);
      setPolicies(Array.isArray(policyRows) ? policyRows : []);
      setGroups(Array.isArray(groupRows) ? groupRows : []);
      setLocations(Array.isArray(locationRows) ? locationRows : []);
      setDevices(Array.isArray(deviceRows) ? deviceRows : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Nachtruhe konnte nicht geladen werden.");
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const visibleDevices = useMemo(() => {
    const q = deviceQuery.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((device) =>
      [device.name, device.serialNumber, device.assetTag, device.udid, device.type]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [devices, deviceQuery]);

  const extraApps = useMemo(
    () => form.allowListedAppBundleIDs.filter((id) => !BUILTIN_IOS_APPS.some((app) => app.bundleId === id)),
    [form.allowListedAppBundleIDs]
  );

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setDeviceQuery("");
  };

  const toggleDay = (id) => {
    setForm((prev) => {
      const has = prev.daysOfWeek.includes(id);
      const daysOfWeek = has ? prev.daysOfWeek.filter((day) => day !== id) : [...prev.daysOfWeek, id].sort((a, b) => a - b);
      return { ...prev, daysOfWeek: daysOfWeek.length ? daysOfWeek : [id] };
    });
  };

  const toggleGroup = (id) => {
    setForm((prev) => {
      const has = prev.groups.includes(id);
      return { ...prev, groups: has ? prev.groups.filter((item) => item !== id) : [...prev.groups, id] };
    });
  };

  const toggleUdid = (udid) => {
    setForm((prev) => {
      const has = prev.udids.includes(udid);
      return { ...prev, udids: has ? prev.udids.filter((item) => item !== udid) : [...prev.udids, udid] };
    });
  };

  const toggleApp = (bundleId) => {
    if (bundleId === "com.apple.Preferences") return;
    setForm((prev) => {
      const has = prev.allowListedAppBundleIDs.includes(bundleId);
      const next = has
        ? prev.allowListedAppBundleIDs.filter((id) => id !== bundleId)
        : [...prev.allowListedAppBundleIDs, bundleId];
      if (!next.includes("com.apple.Preferences")) next.unshift("com.apple.Preferences");
      return { ...prev, allowListedAppBundleIDs: next };
    });
  };

  const addExtraApp = () => {
    const id = form.extraBundleId.trim();
    if (!id) return;
    setForm((prev) => ({
      ...prev,
      extraBundleId: "",
      allowListedAppBundleIDs: prev.allowListedAppBundleIDs.includes(id)
        ? prev.allowListedAppBundleIDs
        : [...prev.allowListedAppBundleIDs, id],
    }));
  };

  const payloadFromForm = () => ({
    name: form.name.trim(),
    enabled: form.enabled,
    timezone: form.timezone.trim() || "Europe/Berlin",
    startTime: form.startTime,
    endTime: form.endTime,
    daysOfWeek: form.daysOfWeek,
    holidayDates: lines(form.holidayDatesText),
    location: form.location || null,
    groups: form.groups,
    udids: form.udids,
    allowListedAppBundleIDs: form.allowListedAppBundleIDs,
    manualOverride: form.manualOverride,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name ist erforderlich.");
      return;
    }
    if (!form.groups.length && !form.udids.length && !form.location) {
      toast.error("Bitte Gruppen, einzelne Geräte oder eine Location zuweisen.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await nightRestService.updatePolicy(editingId, payloadFromForm());
        toast.success("Nachtruhe aktualisiert und synchronisiert.");
      } else {
        await nightRestService.createPolicy(payloadFromForm());
        toast.success("Nachtruhe angelegt. Das Zeitfenster gilt ab der nächsten Minute.");
      }
      resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (policy) => {
    setEditingId(policy._id);
    setForm(formFromPolicy(policy));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const runBusy = async (key, fn, success) => {
    setBusyId(key);
    try {
      await fn();
      if (success) toast.success(success);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Aktion fehlgeschlagen.");
    } finally {
      setBusyId("");
    }
  };

  if (loading && policies.length === 0) {
    return <p className="text-center my-4">Laden…</p>;
  }

  return (
    <div className="container">
      <h2 className="mb-3">Nachtruhe</h2>
      <p className="text-muted mb-4">
        Im Zeitfenster bleibt nur die Allowlist nutzbar (Standard: Einstellungen und Telefon).
        Xavier setzt das DDM-Set <code>xavier.night.*</code> serverseitig, das Gerät wertet keine Uhrzeit selbst aus.
        Zuweisung über <Link to="/device-groups">Geräte-Gruppen</Link> oder einzelne iPhone/iPad.
      </p>

      <form onSubmit={handleSubmit} className="card mb-4 p-3">
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Name *</label>
            <input
              className="form-control"
              value={form.name}
              disabled={!canEdit}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Schul-iPads Nacht"
              required
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Start</label>
            <input
              type="time"
              className="form-control"
              value={form.startTime}
              disabled={!canEdit}
              onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Ende</label>
            <input
              type="time"
              className="form-control"
              value={form.endTime}
              disabled={!canEdit}
              onChange={(e) => setForm((prev) => ({ ...prev, endTime: e.target.value }))}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Zeitzone</label>
            <input
              className="form-control"
              value={form.timezone}
              disabled={!canEdit}
              onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value }))}
            />
          </div>
          <div className="col-md-2 d-flex align-items-end">
            <div className="form-check">
              <input
                id="night-enabled"
                className="form-check-input"
                type="checkbox"
                checked={form.enabled}
                disabled={!canEdit}
                onChange={(e) => setForm((prev) => ({ ...prev, enabled: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="night-enabled">Aktiv</label>
            </div>
          </div>
          <div className="col-12">
            <label className="form-label d-block">Wochentage (Starttag)</label>
            <div className="d-flex flex-wrap gap-2">
              {WEEKDAYS.map((day) => (
                <label key={day.id} className="btn btn-sm btn-outline-secondary mb-0">
                  <input
                    type="checkbox"
                    className="form-check-input me-1"
                    checked={form.daysOfWeek.includes(day.id)}
                    disabled={!canEdit}
                    onChange={() => toggleDay(day.id)}
                  />
                  {day.label}
                </label>
              ))}
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label">Location (optionaler Filter)</label>
            <select
              className="form-select"
              value={form.location}
              disabled={!canEdit}
              onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
            >
              <option value="">alle</option>
              {locations.map((loc) => (
                <option key={loc._id} value={loc._id}>
                  {loc.schoolNumber} – {loc.name}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Manuell</label>
            <select
              className="form-select"
              value={form.manualOverride}
              disabled={!canEdit}
              onChange={(e) => setForm((prev) => ({ ...prev, manualOverride: e.target.value }))}
            >
              <option value="schedule">Zeitplan folgen</option>
              <option value="on">Jetzt sperren</option>
              <option value="off">Jetzt aufheben</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Ferientage (YYYY-MM-DD, eine je Zeile)</label>
            <textarea
              className="form-control font-monospace small"
              rows={3}
              disabled={!canEdit}
              value={form.holidayDatesText}
              onChange={(e) => setForm((prev) => ({ ...prev, holidayDatesText: e.target.value }))}
              placeholder="2026-12-24"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Gruppen</label>
            {groups.length === 0 ? (
              <p className="small text-muted mb-0">Keine Gruppen. Bitte unter Geräte-Gruppen anlegen.</p>
            ) : (
              <div className="border rounded p-2" style={{ maxHeight: 180, overflow: "auto" }}>
                {groups.map((group) => (
                  <label key={group._id} className="form-check d-block">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={form.groups.includes(String(group._id))}
                      disabled={!canEdit}
                      onChange={() => toggleGroup(String(group._id))}
                    />
                    <span className="ms-1">{group.name}</span>
                    {group.memberCount != null ? (
                      <span className="text-muted small"> · {group.memberCount}</span>
                    ) : null}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">Einzelne Geräte (iPhone / iPad)</label>
            <input
              className="form-control form-control-sm mb-2"
              placeholder="Suchen…"
              value={deviceQuery}
              onChange={(e) => setDeviceQuery(e.target.value)}
            />
            <div className="border rounded p-2" style={{ maxHeight: 180, overflow: "auto" }}>
              {visibleDevices.length === 0 ? (
                <p className="small text-muted mb-0">Keine eingeschriebenen iOS-Geräte.</p>
              ) : (
                visibleDevices.map((device) => (
                  <label key={device.udid} className="form-check d-block">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={form.udids.includes(device.udid)}
                      disabled={!canEdit}
                      onChange={() => toggleUdid(device.udid)}
                    />
                    <span className="ms-1">
                      {device.name}
                      <span className="text-muted small">
                        {" "}· {device.type === "ipad" ? "iPad" : "iPhone"}
                        {device.serialNumber ? ` · ${device.serialNumber}` : ""}
                      </span>
                    </span>
                  </label>
                ))
              )}
            </div>
            {form.udids.length > 0 && (
              <p className="small text-muted mb-0 mt-1">{form.udids.length} Gerät(e) gewählt</p>
            )}
          </div>
          <div className="col-12">
            <label className="form-label">Erlaubte Apps während der Nachtruhe</label>
            <div className="row g-2">
              {BUILTIN_IOS_APPS.map((app) => (
                <div className="col-6 col-md-3" key={app.bundleId}>
                  <label className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={form.allowListedAppBundleIDs.includes(app.bundleId)}
                      disabled={!canEdit || app.bundleId === "com.apple.Preferences"}
                      onChange={() => toggleApp(app.bundleId)}
                    />
                    <span className="ms-1">{app.name}</span>
                  </label>
                </div>
              ))}
            </div>
            {extraApps.length > 0 && (
              <div className="mt-2 d-flex flex-wrap gap-2">
                {extraApps.map((id) => (
                  <button
                    key={id}
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    disabled={!canEdit}
                    onClick={() => toggleApp(id)}
                  >
                    {appByBundleId(id)?.name || id} ×
                  </button>
                ))}
              </div>
            )}
            {canEdit && (
              <div className="input-group mt-2" style={{ maxWidth: 420 }}>
                <input
                  className="form-control"
                  placeholder="Weitere Bundle-ID"
                  value={form.extraBundleId}
                  onChange={(e) => setForm((prev) => ({ ...prev, extraBundleId: e.target.value }))}
                />
                <button type="button" className="btn btn-outline-secondary" onClick={addExtraApp}>
                  Hinzufügen
                </button>
              </div>
            )}
          </div>
          {canEdit && (
            <div className="col-12 d-flex gap-2">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? "Speichere…" : (editingId ? "Aktualisieren" : "Hinzufügen")}
              </button>
              {editingId && (
                <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                  Abbrechen
                </button>
              )}
            </div>
          )}
        </div>
      </form>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Richtlinien</h5>
        {canEdit && (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={Boolean(busyId)}
            onClick={() => runBusy("all", () => nightRestService.syncAll(), "Alle Richtlinien synchronisiert.")}
          >
            Jetzt synchronisieren
          </button>
        )}
      </div>

      <table className="table align-middle">
        <thead>
          <tr>
            <th>Name</th>
            <th>Fenster</th>
            <th>Zuweisung</th>
            <th>Status</th>
            <th>Nächster Wechsel</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {policies.map((policy) => {
            const badge = statusBadge(policy);
            return (
              <tr key={policy._id}>
                <td>
                  {policy.name}
                  <div className="small text-muted">
                    {policy.delivery === "ddm" ? "DDM" : "MDM-Profil"} · {policy.setName}
                  </div>
                </td>
                <td>
                  {policy.startTime}–{policy.endTime}
                  <div className="small text-muted">{policy.timezone}</div>
                </td>
                <td>
                  {policy.groupNames?.length ? policy.groupNames.join(", ") : "—"}
                  <div className="small text-muted">
                    {policy.targetCount ?? 0} Gerät(e)
                    {policy.udids?.length ? ` · ${policy.udids.length} einzeln` : ""}
                  </div>
                </td>
                <td>
                  <span className={`badge ${badge.className}`}>{badge.label}</span>
                  {policy.lastError ? (
                    <div className="small text-danger mt-1">{policy.lastError}</div>
                  ) : null}
                </td>
                <td className="small">
                  {formatNextChange(policy.nextChangeAt)}
                  {policy.nextChangeActive != null ? (policy.nextChangeActive ? " · an" : " · aus") : ""}
                </td>
                <td className="text-nowrap">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary me-1"
                    onClick={() => handleEdit(policy)}
                  >
                    Bearbeiten
                  </button>
                  {canEdit && (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary me-1"
                        disabled={Boolean(busyId)}
                        onClick={() => runBusy(`sync-${policy._id}`, () => nightRestService.syncPolicy(policy._id), "Synchronisiert.")}
                      >
                        Sync
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={Boolean(busyId)}
                        onClick={() => {
                          if (!window.confirm("Nachtruhe-Richtlinie löschen und Sperre von den Geräten nehmen?")) return;
                          return runBusy(`del-${policy._id}`, () => nightRestService.deletePolicy(policy._id), "Gelöscht.");
                        }}
                      >
                        Löschen
                      </button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {policies.length === 0 && (
        <p className="text-muted">Noch keine Nachtruhe angelegt.</p>
      )}
    </div>
  );
}
